import React from 'react'
import {BookOpen, Calculator, TrendingUp, Target} from 'lucide-react'

export function HowToSection() {
  return (
    <section className="bg-white rounded-lg shadow-md p-6 md:p-8" aria-labelledby="how-to-heading">
      <h2 id="how-to-heading" className="text-3xl font-bold text-gray-900 mb-6">
        How to Calculate Your SAT Score from a Raw Score
      </h2>
      
      <div className="prose max-w-none text-gray-700 space-y-4 mb-8">
        <p>
          Understanding how to calculate your SAT score is essential for tracking your progress and setting realistic goals. 
          Our <strong>SAT score calculator</strong> makes this process simple and accurate.
        </p>
        
        <p>
          The Digital SAT consists of two main sections: Math and Reading & Writing. Each section is scored 
          on a scale of 200-800, for a total composite score ranging from 400-1600. Your raw score (the number 
          of questions you answered correctly) is converted to a scaled score using the College Board's official 
          conversion tables.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="p-4 bg-blue-50 rounded-lg">
          <Calculator className="w-8 h-8 text-blue-600 mb-3" aria-hidden="true" />
          <h3 className="font-semibold text-gray-900 mb-2">Step 1: Count Correct Answers</h3>
          <p className="text-sm text-gray-600">
            Count how many questions you answered correctly in each section (no penalty for wrong answers).
          </p>
        </div>

        <div className="p-4 bg-green-50 rounded-lg">
          <BookOpen className="w-8 h-8 text-green-600 mb-3" aria-hidden="true" />
          <h3 className="font-semibold text-gray-900 mb-2">Step 2: Use Conversion Table</h3>
          <p className="text-sm text-gray-600">
            Your raw score is converted to a scaled score (200-800) for each section.
          </p>
        </div>

        <div className="p-4 bg-yellow-50 rounded-lg">
          <Target className="w-8 h-8 text-yellow-600 mb-3" aria-hidden="true" />
          <h3 className="font-semibold text-gray-900 mb-2">Step 3: Add Section Scores</h3>
          <p className="text-sm text-gray-600">
            Add your Math and Reading & Writing scaled scores together for your total SAT score.
          </p>
        </div>

        <div className="p-4 bg-purple-50 rounded-lg">
          <TrendingUp className="w-8 h-8 text-purple-600 mb-3" aria-hidden="true" />
          <h3 className="font-semibold text-gray-900 mb-2">Step 4: Track Progress</h3>
          <p className="text-sm text-gray-600">
            Use our calculator regularly to monitor improvement and estimate your sat score over time.
          </p>
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Frequently Asked Questions
        </h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">
              What is a raw score vs. scaled score?
            </h4>
            <p className="text-gray-700 text-sm">
              A raw score is simply the number of questions you answered correctly. A scaled score (200-800 per section) 
              is the standardized score that accounts for slight variations in test difficulty across different test dates.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-2">
              How do I calculate my SAT score manually?
            </h4>
            <p className="text-gray-700 text-sm">
              To calculate my SAT score manually, count correct answers for each section, use the official College Board 
              conversion table to find scaled scores, then add them together. Our score calculator SAT tool does this automatically!
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-2">
              What is a superscore?
            </h4>
            <p className="text-gray-700 text-sm">
              A superscore combines your best section scores from multiple test dates. Many colleges accept superscores, 
              which can significantly improve your chances. Use our SAT superscore calculator to see your best possible score.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-2">
              Is this calculator accurate?
            </h4>
            <p className="text-gray-700 text-sm">
              Yes! Our SAT test score calculator uses official College Board conversion tables to provide accurate score estimates. 
              However, actual test scores may vary slightly based on the specific test form and equating process.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
